import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, CircularProgress, Container, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { gql } from '@apollo/client';
import { client, useStores } from '../../store';
import Settings from '../../store/settings';

const USER_BY_USERNAME_QUERY = gql`
  query UserByUsername($username: String!) {
    user(username: $username) {
      id
      login
      departments
      isForSportObject
      userSettings { id email phone }
      sportObjects { id name }
    }
  }
`;

const decodeJwtPayload = (token: string): { login?: string } => {
  const payloadPart = token.split('.')[1];
  if (!payloadPart) throw new Error('Некорректный формат токена.');
  const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  return JSON.parse(atob(padded)) as { login?: string };
};

const SSOPage = observer(() => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { userStore } = useStores();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const authError = searchParams.get('authError');
    const authCode = searchParams.get('authCode');
    const returnUrl = searchParams.get('returnUrl') || '/equipments';

    if (authError) {
      setError(
        authError === 'access_denied'
          ? 'Вход выполнен, но у вашей учетной записи нет доступа к учету оборудования.'
          : searchParams.get('authErrorDescription') || 'Центр авторизации отклонил вход.',
      );
      return;
    }
    if (!authCode) {
      setError('Код SSO сессии отсутствует.');
      return;
    }

    const completeLogin = async () => {
      const response = await fetch(`${Settings.server}/auth/sso/session`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: authCode }),
      });
      if (!response.ok) throw new Error('Не удалось завершить SSO вход.');
      const session = (await response.json()) as { token?: string };
      if (!session.token) throw new Error('SSO не вернул локальную сессию.');

      localStorage.setItem('user_token', session.token);
      localStorage.removeItem('user_logged_out');
      const payload = decodeJwtPayload(session.token);
      if (!payload.login) throw new Error('SSO токен не содержит логин.');

      const userResponse = await client.query({
        query: USER_BY_USERNAME_QUERY,
        variables: { username: payload.login },
        fetchPolicy: 'network-only',
        context: { headers: { Authorization: `Bearer ${session.token}` } },
      });
      if (!userResponse.data?.user) throw new Error('Пользователь Mossport не найден.');
      userStore.setUser({ token: session.token, user: userResponse.data.user });
      navigate(returnUrl.startsWith('/') && !returnUrl.startsWith('//') ? returnUrl : '/', {
        replace: true,
      });
    };

    completeLogin().catch((cause: unknown) => {
      localStorage.removeItem('user_token');
      userStore.clearUser({ revokeRemote: false, manual: false });
      setError(cause instanceof Error ? cause.message : 'Не удалось войти через SSO.');
    });
  }, [navigate, searchParams, userStore]);

  return (
    <Container
      maxWidth="sm"
      sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', textAlign: 'center' }}
    >
      {error ? (
        <Alert
          severity="error"
          action={(
            <Button color="inherit" size="small" onClick={() => navigate('/login')}>
              Повторить
            </Button>
          )}
        >
          {error}
        </Alert>
      ) : (
        <div>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Завершаем вход через Auth Center…</Typography>
        </div>
      )}
    </Container>
  );
});

export default SSOPage;
